import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

let authToken = null;
let _onUnauthorized = null;

/**
 * Register a callback that fires whenever the server returns 401/403
 * (expired or invalid token). Called from useAuth to wire up auto-logout.
 */
export function setUnauthorizedHandler(fn) {
  _onUnauthorized = fn;
}

// Global response interceptor — triggers auto-logout on an expired or invalid
// session.
//
// "Your session is dead" and "you may not do that" are different answers, and
// only the first is a reason to sign someone out. The server marks the first
// with code "SESSION_INVALID" (see backend/middleware/auth.js); anything else
// is an authorisation refusal that the calling screen should handle itself.
//
// Treating every 403 as a dead session — which is what this used to do — signed
// people out for merely touching something that was not theirs: a subadmin
// opening an admin-only endpoint, or a guest whose project list is closed to
// them.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;
    const url = error.config?.url || "";
    // Sign-in endpoints report their own failures; a bad password is not a
    // dead session. The access-link routes are sign-in too — a wrong OTP must
    // not tear down the page the user is typing into.
    const isAuthRoute =
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/access-link/");

    const sessionIsDead =
      status === 401 || (status === 403 && code === "SESSION_INVALID");

    if (sessionIsDead && !isAuthRoute && _onUnauthorized) {
      _onUnauthorized();
    }
    return Promise.reject(error);
  },
);

export function setAuthToken(token) {
  authToken = token;
  if (token) {
    // if token exists → attach it to EVERY future axios request automatically
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    // if token is null/removed → stop sending Authorization header
    delete axios.defaults.headers.common["Authorization"];
  }
}

const api = {
  // Auth
  login: (username, password) =>
    axios.post(`${API_BASE}/auth/login`, { username, password }),
  register: (username, email, password, phoneNumber) =>
    axios.post(`${API_BASE}/auth/register`, {
      username,
      email,
      password,
      phoneNumber,
    }),
  logout: () => axios.post(`${API_BASE}/auth/logout`),

  // Guest access links — passwordless sign-in. No auth header: the link token
  // in the body is the only credential these three accept, and the OTP that
  // follows is what actually proves identity.
  accessLinkLookup: (token) =>
    axios.post(`${API_BASE}/auth/access-link/lookup`, { token }),
  accessLinkRequestOtp: (token) =>
    axios.post(`${API_BASE}/auth/access-link/request-otp`, { token }),
  accessLinkVerify: (token, otp) =>
    axios.post(`${API_BASE}/auth/access-link/verify`, { token, otp }),
  acceptTerms: (version) =>
    axios.post(`${API_BASE}/auth/accept-terms`, { version }),
  submitTrialFeedback: (data) =>
    axios.post(`${API_BASE}/auth/trial-feedback`, data),
  getTrialStatus: () => axios.get(`${API_BASE}/auth/trial-status`),

  // Agents
  getAgents: () => axios.get(`${API_BASE}/agents`),

  getAgentDetails: (agentId) => axios.get(`${API_BASE}/agents/${agentId}`),

  executeAgent: (agentId, params, subcommand, resumeSessionId, projectId) =>
    axios.post(`${API_BASE}/agents/${agentId}/execute`, {
      params,
      subcommand,
      ...(resumeSessionId ? { resumeSessionId } : {}),
      ...(projectId ? { projectId } : {}),
    }),

  // Executions
  getExecutions: (limit = 50, projectId = null) =>
    axios.get(`${API_BASE}/executions`, {
      params: { limit, ...(projectId ? { projectId } : {}) },
    }),

  getExecution: (jobId) => axios.get(`${API_BASE}/executions/${jobId}`),

  getExecutionChildren: (parentJobId) =>
    axios.get(`${API_BASE}/executions/${parentJobId}/children`),

  deleteExecution: (jobId) => axios.delete(`${API_BASE}/executions/${jobId}`),

  downloadExecutionFile: async (executionId, filename) => {
    const response = await axios.get(
      `${API_BASE}/executions/${executionId}/files/${encodeURIComponent(filename)}`,
      { responseType: "blob" },
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Projects (tenant API key management)
  // Target Location dropdown options (server owns the canonical list)
  getProjectLocations: () => axios.get(`${API_BASE}/projects/locations`),
  getProjects: () => axios.get(`${API_BASE}/projects`),
  getProject: (id) => axios.get(`${API_BASE}/projects/${id}`),
  createProject: (data) => axios.post(`${API_BASE}/projects`, data),
  updateProject: (id, data) => axios.patch(`${API_BASE}/projects/${id}`, data),
  activateProject: (id) => axios.patch(`${API_BASE}/projects/${id}/activate`),
  deleteProject: (id) => axios.delete(`${API_BASE}/projects/${id}`),

  // Score dashboard — per-agent score history for a project.
  // `contract` is the server's own list of scored agents, labels and
  // thresholds; render from it rather than hard-coding a second copy that can
  // drift out of sync with the backend.
  getDashboardContract: () => axios.get(`${API_BASE}/dashboard/contract`),
  // `page` selects which page the pages.agents list describes (e.g. "/pricing").
  // Omitted means the server picks the most recently audited page.
  getProjectDashboard: (projectId, page = null) =>
    axios.get(`${API_BASE}/dashboard/projects/${projectId}`, {
      params: page ? { page } : {},
    }),
  getProjectScorecards: (projectId, limit = 50) =>
    axios.get(`${API_BASE}/dashboard/projects/${projectId}/scorecards`, {
      params: { limit },
    }),

  // --- CMS execution ------------------------------------------------------
  // Applying audit fixes to a live site. `getCmsRegistry` returns the
  // backend's own list of change types and reason codes — render labels from
  // it rather than keeping a second copy here, which would drift from the
  // rules the applier actually enforces.
  getCmsRegistry: () => axios.get(`${API_BASE}/cms/registry`),

  // A plain URL rather than a request: this is rendered as a link so the owner
  // can hand it to whoever actually has access to wp-admin.
  cmsPluginDownloadUrl: () => `${API_BASE}/public/cms/plugin`,

  getCmsConnection: (projectId) =>
    axios.get(`${API_BASE}/cms/projects/${projectId}/connection`),
  startCmsPairing: (projectId, provider = "wordpress") =>
    axios.post(`${API_BASE}/cms/projects/${projectId}/connection/pairing`, { provider }),
  saveCmsCredentials: (projectId, data) =>
    axios.put(`${API_BASE}/cms/projects/${projectId}/connection`, data),
  verifyCmsConnection: (projectId) =>
    axios.post(`${API_BASE}/cms/projects/${projectId}/connection/verify`),
  setCmsWritesEnabled: (projectId, enabled) =>
    axios.post(`${API_BASE}/cms/projects/${projectId}/connection/writes`, { enabled }),
  disconnectCms: (projectId) =>
    axios.delete(`${API_BASE}/cms/projects/${projectId}/connection`),

  getChangeset: (executionId) =>
    axios.get(`${API_BASE}/cms/changesets/${executionId}`),
  listChangesets: (projectId, limit = 50) =>
    axios.get(`${API_BASE}/cms/projects/${projectId}/changesets`, { params: { limit } }),
  decideChangeItem: (executionId, itemId, decision) =>
    axios.post(`${API_BASE}/cms/changesets/${executionId}/items/${itemId}/decision`, { decision }),
  approveAllChanges: (executionId) =>
    axios.post(`${API_BASE}/cms/changesets/${executionId}/approve-all`),
  applyChangeset: (executionId) =>
    axios.post(`${API_BASE}/cms/changesets/${executionId}/apply`),
  revertChangeItem: (executionId, itemId) =>
    axios.post(`${API_BASE}/cms/changesets/${executionId}/items/${itemId}/revert`),

  getCmsLog: (projectId, params = {}) =>
    axios.get(`${API_BASE}/cms/projects/${projectId}/log`, { params }),

  getExecutionFileContent: (executionId, filename) =>
    axios
      .get(`${API_BASE}/executions/${executionId}/files/${encodeURIComponent(filename)}`, {
        responseType: "text",
        headers: { Accept: "text/markdown, text/plain, */*" },
      })
      .then((r) => r.data),

  // Profile
  updateProfile: (userId, data) =>
    axios.put(`${API_BASE}/users/${userId}`, data),
  getProfile: () => axios.get(`${API_BASE}/users/me`),

  // Admin — stats
  adminGetStats: () => axios.get(`${API_BASE}/admin/stats`),

  // Admin — users
  adminGetUsers: () => axios.get(`${API_BASE}/admin/users`),
  adminGetUser: (id) => axios.get(`${API_BASE}/admin/users/${id}`),
  adminCreateUser: (data) => axios.post(`${API_BASE}/admin/users`, data),
  adminUpdateUser: (id, data) => axios.put(`${API_BASE}/admin/users/${id}`, data),
  adminDeleteUser: (id) => axios.delete(`${API_BASE}/admin/users/${id}`),
  adminRevokeUserSessions: (id) =>
    axios.post(`${API_BASE}/admin/users/${id}/revoke-sessions`),

  // Admin — agents
  adminGetAgents: () => axios.get(`${API_BASE}/admin/agents`),
  adminGetAgent: (id) => axios.get(`${API_BASE}/admin/agents/${id}`),
  adminCreateAgent: (data) => axios.post(`${API_BASE}/admin/agents`, data),
  adminUpdateAgent: (id, data) => axios.put(`${API_BASE}/admin/agents/${id}`, data),
  adminDeleteAgent: (id) => axios.delete(`${API_BASE}/admin/agents/${id}`),
  // order: array of agent ids in the sequence they should appear to users
  adminReorderAgents: (order) =>
    axios.put(`${API_BASE}/admin/agents/reorder`, { order }),

  // Admin — sessions
  adminGetSessions: (params) =>
    axios.get(`${API_BASE}/admin/sessions`, { params }),
  adminRevokeSession: (sessionId) =>
    axios.delete(`${API_BASE}/admin/sessions/${sessionId}`),

  // Admin — executions
  adminGetExecutions: (params) =>
    axios.get(`${API_BASE}/admin/executions`, { params }),
  adminGetExecution: (jobId) =>
    axios.get(`${API_BASE}/admin/executions/${jobId}`),
  adminDeleteExecution: (jobId) =>
    axios.delete(`${API_BASE}/admin/executions/${jobId}`),

  // Admin/subadmin — guest access links
  adminGetAccessLinkOptions: () =>
    axios.get(`${API_BASE}/admin/access-links/options`),
  adminGetAccessLinks: () => axios.get(`${API_BASE}/admin/access-links`),
  adminCreateAccessLink: (data) =>
    axios.post(`${API_BASE}/admin/access-links`, data),
  adminResendAccessLink: (id) =>
    axios.post(`${API_BASE}/admin/access-links/${id}/resend`),
  // days: how much longer to give them; notify: email them about it
  adminExtendAccessLink: (id, { days, notify }) =>
    axios.post(`${API_BASE}/admin/access-links/${id}/extend`, { days, notify }),
  adminRevokeAccessLink: (id) =>
    axios.delete(`${API_BASE}/admin/access-links/${id}`),

  // Admin — projects master
  adminGetProjects: (params) =>
    axios.get(`${API_BASE}/admin/projects`, { params }),
  // Admin override for a project's locked main domain / competitors
  adminUpdateProjectDomains: (id, data) =>
    axios.patch(`${API_BASE}/admin/projects/${id}`, data),
  // Soft-delete — the only route that can remove a domain-locked project
  adminDeleteProject: (id) => axios.delete(`${API_BASE}/admin/projects/${id}`),

  /**
   * Stream execution updates (SSE) using fetch + ReadableStream so chunks are
   * applied as they arrive. EventSource + CRA dev proxy often buffers until EOF.
   */
  streamExecution: (jobId, onMessage, onError) => {
    const qs = authToken ? `?token=${encodeURIComponent(authToken)}` : "";
    const url = `${API_BASE}/executions/${jobId}/stream${qs}`;
    const abortController = new AbortController();
    let intentionallyClosed = false;

    const parseSseDataLines = (block) => {
      for (const line of block.split("\n")) {
        if (!line.startsWith("data")) continue;
        const payload = line.startsWith("data: ")
          ? line.slice(6)
          : line.startsWith("data:")
            ? line.slice(5).trimStart()
            : "";
        if (!payload) continue;
        try {
          onMessage(JSON.parse(payload));
        } catch (err) {
          console.error("Error parsing SSE message:", err);
        }
      }
    };

    (async () => {
      try {
        let crossOrigin = false;
        if (typeof window !== "undefined" && API_BASE.startsWith("http")) {
          try {
            const apiOrigin = new URL(API_BASE, window.location.origin).origin;
            crossOrigin = apiOrigin !== window.location.origin;
          } catch {
            crossOrigin = false;
          }
        }
        const response = await fetch(url, {
          method: "GET",
          signal: abortController.signal,
          credentials: crossOrigin ? "include" : "same-origin",
          headers: { Accept: "text/event-stream" },
        });
        if (!response.ok) {
          throw new Error(`Stream failed: ${response.status}`);
        }
        if (!response.body) {
          throw new Error("Stream has no body");
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { done, value } = await reader.read();
          if (done) {
            buffer += decoder.decode();
            buffer = buffer.replace(/\r\n/g, "\n");
            let sep;
            while ((sep = buffer.indexOf("\n\n")) !== -1) {
              const block = buffer.slice(0, sep);
              buffer = buffer.slice(sep + 2);
              parseSseDataLines(block);
            }
            if (buffer.trim()) {
              parseSseDataLines(buffer.trimEnd());
            }
            break;
          }
          buffer += decoder
            .decode(value, { stream: true })
            .replace(/\r\n/g, "\n");
          let sep;
          while ((sep = buffer.indexOf("\n\n")) !== -1) {
            const block = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            parseSseDataLines(block);
          }
        }
      } catch (err) {
        if (intentionallyClosed || err?.name === "AbortError") return;
        console.error("SSE stream error:", err);
        onError?.(err);
      }
    })();

    return {
      close: () => {
        intentionallyClosed = true;
        abortController.abort();
      },
    };
  },
};

export default api;
